import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { getCustomerSuccess } from "../../redux/Profile/profileSlice";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Link,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useAppTheme } from "../../theme/useAppTheme";
import { pageBackground, primaryButtonSx } from "../../theme/resolveThemeColors";

const OTP_LENGTH = 4;

function otpDataText(data) {
  if (data == null) return "";
  if (typeof data === "string" || typeof data === "number") return String(data);
  return "";
}

export default function GenerateOtp() {
  const location = useLocation();
  const auth = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const otpNotice = otpDataText(location.state?.otpData);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLength] = useState(OTP_LENGTH);
  const otpInputRef = useRef(null);
  const themeColors = useAppTheme();
  const statePhone = location.state?.phone;
  const [phone, setPhone] = useState(
    statePhone || localStorage.getItem("otpPhone") || ""
  );

  useEffect(() => {
    if (statePhone) {
      localStorage.setItem("otpPhone", statePhone);
    }
  }, [statePhone]);

  useEffect(() => {
    if (!otpNotice) return;
    toast.success(otpNotice);
  }, [otpNotice]);

  useEffect(() => {
    otpInputRef.current?.focus();
  }, []);

  //  Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/otp-generator/generateotp`,
        {
          otpSrc: phone,
          otpType: "LOGIN",
          otpFor: "Customer",
          jeweller: process.env.REACT_APP_JEWELLER_ID,
        },
        {
          headers: { version: "1.1.1" },
        }
      );

      if (response.data?.success) {
        const notice = otpDataText(response.data?.data);
        if (notice) toast.success(notice);
        setOtp("");
        otpInputRef.current?.focus();
      } else {
        toast.error(response.data?.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error(error.response?.data?.message || "Resend OTP failed");
    } finally {
      setLoading(false);
    }
  };

  const saveTokens = (accessToken, refreshToken) => {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("adminAccessToken", accessToken);
    }
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("adminRefreshToken", refreshToken);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/otp-generator/verifyOtp`,
        {
          otpSrc: phone,
          otpType: "LOGIN",
          otp: Number(otp),
          otpFor: "Customer",
          jeweller: process.env.REACT_APP_JEWELLER_ID,
        },
        {
          headers: { version: "1.1.1" },
        }
      );

      if (!response.data?.success) {
        toast.error(response.data?.message || "OTP verification failed");
        return;
      }

      const otpData = response.data?.data || {};
      saveTokens(otpData.accessToken, otpData.refreshToken);

      const customerResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/getCustomer`,
        {
          phone,
          jeweller: process.env.REACT_APP_JEWELLER_ID,
          deviceDetails: navigator.userAgent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            version: "1.2.0",
            newui: "true",
          },
        }
      );

      const customerPayload = customerResponse.data?.data || {};
      const customer = customerPayload.customer;

      if (customerResponse.data?.success && customer) {
        const accessToken = customerPayload.accessToken || otpData.accessToken;
        const refreshToken =
          customerPayload.refreshToken || otpData.refreshToken;
        saveTokens(accessToken, refreshToken);
        localStorage.setItem("user", JSON.stringify(customer));
        dispatch(getCustomerSuccess(customerResponse.data));

        auth.login(
          {
            username: phone,
            rememberMe: true,
            login: "customer",
          },
          (message) => toast.error(message),
          {
            data: {
              data: {
                accessToken,
                refreshToken,
                customer,
              },
            },
          }
        );
        return;
      }

      navigate("/Register");
    } catch (error) {
      console.error("Verify OTP error:", error);
      toast.error(error.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: pageBackground(themeColors),
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: themeColors.scaffoldText,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 542,
          borderRadius: 3,
          boxShadow: 6,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            textAlign="left"
            gutterBottom
            sx={{
              fontSize: { xs: "1.8rem", sm: "48px" },
              fontWeight: 500,
              color: themeColors.scaffoldText,
            }}
          >
            Welcome Back!
            <Box
              component="span"
              sx={{
                display: "block",
                color: themeColors.primary,
                fontSize: { xs: "1.8rem", sm: "42px" },
                fontWeight: 500,
              }}
            >
              Login with OTP
            </Box>
          </Typography>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              gap: 1.5,
              mb: 3,
              cursor: "text",
              "@keyframes otpCaretBlink": {
                "0%, 49%": { opacity: 1 },
                "50%, 100%": { opacity: 0 },
              },
            }}
            onClick={() => otpInputRef.current?.focus()}
          >
            {Array.from({ length: otpLength }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 56,
                  height: 60,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 600,
                  color: themeColors.scaffoldText,
                  border: `1px solid ${
                    otp[index] ? themeColors.primary : "#cfcfcf"
                  }`,
                  cursor: "text",
                }}
              >
                {otp[index] ?? ""}
                {index === Math.min(otp.length, otpLength - 1) && (
                  <Box
                    component="span"
                    sx={{
                      width: "2px",
                      height: 24,
                      ml: otp[index] ? "2px" : 0,
                      bgcolor: themeColors.scaffoldText,
                      animation: "otpCaretBlink 1s step-end infinite",
                    }}
                  />
                )}
              </Box>
            ))}
            <input
              ref={otpInputRef}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= otpLength) setOtp(value);
              }}
              style={{
                position: "absolute",
                opacity: 0,
                width: "100%",
                height: "100%",
                border: "none",
                cursor: "text",
              }}
            />
          </Box>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Grid>
              <Link
                component="button"
                onClick={() => navigate("/login")}
                underline="none"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: themeColors.scaffoldText,
                  fontWeight: 500,
                  fontSize: { xs: "0.9rem", sm: "16px" },
                  cursor: "pointer",
                }}
              >
                <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
                Change Phone Number
              </Link>
            </Grid>

            <Grid>
              <Link
                href="#"
                underline="hover"
                sx={{
                  color: themeColors.primary,
                  fontWeight: 500,
                  fontSize: { xs: "0.9rem", sm: "16px" },
                }}
                onClick={handleResendOtp}
              >
                Resend OTP
              </Link>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            fullWidth
            sx={primaryButtonSx(themeColors, {
              fontWeight: "bold",
              py: 1.5,
              fontSize: "1rem",
            })}
            onClick={handleVerifyOtp}
            disabled={loading || otp.length !== otpLength}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Verify OTP"
            )}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
